import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ArrowLeft } from "lucide-react";
import { useCallback, useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
  ApiError,
  getInquiry,
  type DetectionEventResponse,
  type InquiryStatus,
} from "@/api";
import {
  ANCHOR_INQUIRY_CREATED,
  ANCHOR_INQUIRY_FINALIZED,
  EventTimeline,
  captureAnchor,
} from "@/components/EventTimeline";
import { SeverityBadge } from "@/components/SeverityBadge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

const PULSE_DURATION_MS = 2000;

export function InquiryDrillDown(): JSX.Element {
  const { inquiry_id: inquiryId } = useParams<{ inquiry_id: string }>();

  if (!inquiryId) {
    return <NotFoundShell message="Missing inquiry id in URL." />;
  }

  return <InquiryDrillDownInner inquiryId={inquiryId} />;
}

function InquiryDrillDownInner({
  inquiryId,
}: {
  inquiryId: string;
}): JSX.Element {
  // Spec: "Inquiry drill-down does not poll; it fetches once per page
  // load." Drill-down responses are heavy; refresh-the-page is the
  // intended way to get fresh data.
  const query = useQuery({
    queryKey: ["inquiry", inquiryId],
    queryFn: () => getInquiry(inquiryId),
    refetchInterval: false,
    retry: (failureCount, error) => {
      // 404 is a stable answer: don't retry. Other errors get the
      // default retry behaviour (3 attempts).
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 3;
    },
  });

  const [pulsedId, setPulsedId] = useState<string | null>(null);

  const triggerPulse = useCallback((anchorId: string) => {
    const element = document.getElementById(anchorId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setPulsedId(anchorId);
    window.setTimeout(() => setPulsedId(null), PULSE_DURATION_MS);
  }, []);

  if (query.isLoading) {
    return (
      <main className="container py-8">
        <BackLink />
        <p className="mt-4 text-sm text-muted-foreground">Loading inquiry…</p>
      </main>
    );
  }

  if (query.error instanceof ApiError && query.error.status === 404) {
    return <NotFoundShell message={`Inquiry ${inquiryId} not found.`} />;
  }

  if (query.error) {
    return (
      <main className="container py-8">
        <BackLink />
        <ErrorBanner error={query.error} className="mt-4" />
      </main>
    );
  }

  const data = query.data;
  if (!data) {
    return <NotFoundShell message="Inquiry payload was empty." />;
  }

  const inquiryCreatedAt =
    data.inquiry_created?.captured_at ??
    data.inquiry_finalized?.captured_at ??
    null;

  return (
    <main className="container space-y-6 py-8">
      <header className="space-y-2">
        <BackLink />
        <div className="flex flex-wrap items-baseline gap-3">
          <h1 className="font-mono text-xl font-semibold tracking-tight">
            Inquiry {data.inquiry_id}
          </h1>
          <StatusLabel status={data.status} />
        </div>
        {inquiryCreatedAt !== null && (
          <p className="text-sm text-muted-foreground">
            Created{" "}
            <span title={inquiryCreatedAt}>
              {formatDistanceToNow(new Date(inquiryCreatedAt), {
                addSuffix: true,
              })}
            </span>
          </p>
        )}
      </header>

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <EventTimeline
          inquiryCreated={data.inquiry_created}
          captures={data.captures}
          inquiryFinalized={data.inquiry_finalized}
          pulsedId={pulsedId}
        />
        <DetectionPanel
          detections={data.detections}
          onJump={triggerPulse}
        />
      </div>
    </main>
  );
}

function StatusLabel({ status }: { status: InquiryStatus }): JSX.Element | null {
  // Per spec: in_flight → "In flight"; stuck → "Stuck — see detections";
  // finalized → no label (the timeline tells the story); partial →
  // "Partial reconstruction — inquiry creation event was lost."
  switch (status) {
    case "finalized":
      return null;
    case "in_flight":
      return (
        <span className="inline-flex items-center rounded-md border border-blue-200 bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800">
          In flight
        </span>
      );
    case "stuck":
      return (
        <span className="inline-flex items-center rounded-md border border-orange-200 bg-orange-100 px-2.5 py-0.5 text-xs font-semibold text-orange-800">
          Stuck — see detections
        </span>
      );
    case "partial":
      return (
        <span className="inline-flex items-center rounded-md border border-orange-200 bg-orange-100 px-2.5 py-0.5 text-xs font-semibold text-orange-800">
          Partial reconstruction — inquiry creation event was lost
        </span>
      );
  }
}

function DetectionPanel({
  detections,
  onJump,
}: {
  detections: DetectionEventResponse[];
  onJump: (anchorId: string) => void;
}): JSX.Element {
  return (
    <Card className="lg:sticky lg:top-6 lg:h-fit">
      <CardHeader>
        <CardTitle>Detections</CardTitle>
      </CardHeader>
      <CardContent>
        {detections.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No detection events for this inquiry.
          </p>
        ) : (
          <ul className="space-y-3">
            {detections.map((detection) => (
              <li key={detection.id}>
                <button
                  type="button"
                  onClick={() => onJump(anchorForDetection(detection))}
                  className="block w-full rounded-md border border-input bg-background p-3 text-left transition-colors hover:bg-accent"
                >
                  <div className="mb-1 flex items-center gap-2">
                    <SeverityBadge severity={detection.severity} />
                    <span className="text-xs text-muted-foreground">
                      {detection.rule_id}
                    </span>
                  </div>
                  <p className="text-sm">{detection.message}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    <span title={detection.detected_at}>
                      {formatDistanceToNow(new Date(detection.detected_at), {
                        addSuffix: true,
                      })}
                    </span>
                  </p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Pick which timeline card a detection should scroll-and-pulse to. The
 * spec asks for "the relevant timeline card"; this is the per-rule
 * mapping that makes that concrete.
 *
 * - Rules carrying ``capture_event_id`` in details → that capture card.
 * - Rules whose evidence is the inquiry-created event → created card.
 * - Rules whose evidence is the dispatcher's completion → finalized card.
 * - Anything else → created card (a sensible default that doesn't
 *   silently fail when a new rule is added).
 */
function anchorForDetection(detection: DetectionEventResponse): string {
  const captureId = detection.details?.capture_event_id;
  if (typeof captureId === "number") {
    return captureAnchor(captureId);
  }
  switch (detection.rule_id) {
    case "lambda_invocation_failure":
    case "missing_dealership_id":
    case "handler_misconfiguration":
      return ANCHOR_INQUIRY_FINALIZED;
    case "contact_point_gate":
      return ANCHOR_INQUIRY_CREATED;
    default:
      return ANCHOR_INQUIRY_CREATED;
  }
}

function BackLink(): JSX.Element {
  return (
    <Link
      to="/"
      className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="mr-1 h-4 w-4" />
      Back to detections
    </Link>
  );
}

function NotFoundShell({ message }: { message: string }): JSX.Element {
  return (
    <main className="container py-8">
      <BackLink />
      <Card className="mt-4">
        <CardContent className="py-8 text-center">
          <p className="text-sm text-muted-foreground">{message}</p>
          <Button asChild className="mt-4" variant="outline" size="sm">
            <Link to="/">Return to detections list</Link>
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}

function ErrorBanner({
  error,
  className,
}: {
  error: unknown;
  className?: string;
}): JSX.Element {
  const message =
    error instanceof ApiError
      ? `Request failed: ${error.status} ${error.message}`
      : error instanceof Error
        ? error.message
        : "Unknown error";
  return (
    <div
      className={cn(
        "rounded-md border border-destructive/50 bg-destructive/10 px-4 py-2 text-sm text-destructive",
        className,
      )}
    >
      {message}
    </div>
  );
}
